import { useEffect, useState } from 'react';
import { Card, Box, Autocomplete, TextField, useTheme } from '@mui/material';
import { AxisColorPicker } from './axis-color-picker';
import { YAxisRangePicker } from './components/y-axis-range-picker';
import { useGetAllTagsQuery } from 'src/hooks/reactQueryApiHooks';
import { useAuthentication } from 'src/features/authentication/context';
import {
  AggregationOption,
  AggregationOptionLabel,
  AggregationSelection,
  AxisItemConfig,
  StatisticOption,
  StatisticOptionLabels,
  StatisticSelection,
} from 'src/types/trends/trends';
import { CloseIconV2 } from 'src/assets/icons/CloseIconV2';
import { NewSite } from 'src/types/sites/sites';
import {
  card,
  siteRowWrapper,
  selectFieldBox,
  smallFieldBox,
  colorBox,
  colorSwatch,
} from './style';

type Props = {
  inputId: string;
  siteId: string;
  tagId: string;
  aggregation: AggregationSelection | null;
  statistic: StatisticSelection | null;
  onChangeItem: (id: string, patch: Partial<AxisItemConfig>) => void;
  onRemove: (id: string) => void;
  color: string;
  canRemove: boolean;
  yAxisAuto?: boolean;
  yAxisFrom?: number | null;
  yAxisTo?: number | null;
  fixedSiteId?: string;
};

export const AxisDialogItem = ({
  inputId,
  siteId,
  tagId,
  aggregation,
  statistic,
  onChangeItem,
  onRemove,
  color,
  canRemove,
  yAxisAuto = true,
  yAxisFrom = null,
  yAxisTo = null,
  fixedSiteId,
}: Props) => {
  const {
    customerId: { value: customerId },
    sites,
  } = useAuthentication();

  const theme = useTheme();
  const [selectedSite, setSelectedSite] = useState<NewSite | null>(
    () => sites?.value?.find((s: NewSite) => s.id === (siteId || fixedSiteId)) ?? null
  );

  const { tags } = useGetAllTagsQuery({
    customerId: customerId!,
    siteId: selectedSite?.id,
  });

  const selectedTagOption = tags?.find((t: { id: string }) => t.id === tagId) ?? null;

  // Filter hidden tags
  const visibleTags = tags?.filter((t: { hidden?: boolean }) => !t.hidden) ?? [];

  const aggregationOptions: AggregationSelection[] = ['Auto', ...Object.values(AggregationOption)];

  // Statistic ("Aggregation" dropdown) is not applicable when the interval is "Auto" or "All data"
  const isStatisticDisabled = aggregation === 'Auto' || aggregation === AggregationOption.null;
  const statisticOptions: StatisticSelection[] = [
    'Auto',
    'On-change',
    'Min-Average-Max',
    ...Object.values(StatisticOption),
  ];

  // Keep selectedSite in sync when sites load asynchronously
  useEffect(() => {
    if (!selectedSite && sites?.value) {
      const resolvedId = siteId || fixedSiteId;
      const site = sites.value.find((s: NewSite) => s.id === resolvedId) ?? null;
      if (site) setSelectedSite(site);
    }
  }, [sites?.value]); // eslint-disable-line

  // Sync fixed site info to parent state once site resolves
  useEffect(() => {
    if (fixedSiteId && selectedSite) {
      onChangeItem(inputId, { siteId: selectedSite.id, siteName: selectedSite.siteName });
    }
  }, [fixedSiteId, selectedSite?.id]); // eslint-disable-line

  const closeButton = (sx: object) =>
    canRemove ? (
      <Box
        sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', ...sx }}
        onClick={() => onRemove(inputId)}
      >
        <CloseIconV2 fill={theme.palette.grey[600]} />
      </Box>
    ) : null;

  return (
    <Card sx={card}>
      {/* On small screens this row contains: color picker + site autocomplete + close button */}
      <Box sx={siteRowWrapper}>
        <Box sx={colorBox}>
          <Box sx={colorSwatch(color)} />
          <AxisColorPicker color={color} onChange={(color) => onChangeItem(inputId, { color })} />
        </Box>

        {!fixedSiteId && (
          <Box sx={selectFieldBox}>
            <Autocomplete
              sx={{ flex: 1 }}
              size="small"
              value={selectedSite}
              onChange={(_, site: NewSite | null) => {
                setSelectedSite(site);
                onChangeItem(inputId, { siteId: site?.id ?? '', siteName: site?.siteName ?? '' });
              }}
              renderInput={(params) => (
                <TextField {...params} label="Select Site" data-sm="select-site-input" />
              )}
              options={sites?.value ?? []}
              getOptionLabel={(option) => option.siteName}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.siteName}
                </li>
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Box>
        )}

        {/* Close button visible only on screens < 768px */}
        {closeButton({
          ml: 'auto',
          display: 'none',
          '@media (max-width: 767px)': { display: 'flex' },
        })}
      </Box>

      <Box sx={selectFieldBox}>
        <Autocomplete
          sx={{ flex: 1 }}
          size="small"
          value={selectedTagOption}
          onChange={(_, selectedTag: any) => {
            if (selectedTag) {
              onChangeItem(inputId, {
                tagId: selectedTag.id ?? '',
                inputName: selectedTag.displayName ?? '',
                rawInputName: selectedTag.rawInputName ?? '',
                gatewayId: selectedTag.gatewayId ?? '',
                unit: selectedTag.rawUnit ?? '',
                dataType: selectedTag.dataType ?? '',
              });
            }
          }}
          renderInput={(params) => (
            <TextField {...params} label="Select Tag" data-sm="select-tag-input" />
          )}
          options={visibleTags}
          getOptionLabel={(option) => option?.displayName}
          renderOption={(props, option) => (
            <li {...props} key={`${option.id}-${option.displayName}`}>
              {option.displayName}
            </li>
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          disabled={!selectedSite}
        />
      </Box>

      <Box sx={smallFieldBox}>
        <Autocomplete<AggregationSelection>
          sx={{ flex: 1 }}
          size="small"
          value={aggregation || null}
          onChange={(_, aggregation) => {
            if (aggregation) {
              onChangeItem(inputId, {
                aggregation,
                ...((aggregation === 'Auto' || aggregation === AggregationOption.null) && {
                  statistic: 'Auto',
                }),
              });
            }
          }}
          renderInput={(params) => (
            <TextField {...params} label="Interval" data-sm="interval-input" />
          )}
          options={aggregationOptions}
          getOptionLabel={(option) => AggregationOptionLabel[option]}
          renderOption={(props, option) => (
            <li {...props} key={option}>
              {AggregationOptionLabel[option]}
            </li>
          )}
          isOptionEqualToValue={(option, value) => option === value}
        />
      </Box>
      <Box sx={smallFieldBox}>
        <Autocomplete<StatisticSelection>
          sx={{ flex: 1 }}
          size="small"
          value={isStatisticDisabled ? 'Auto' : statistic || null}
          disabled={isStatisticDisabled}
          onChange={(_, statistic) => {
            if (statistic) {
              onChangeItem(inputId, { statistic });
            }
          }}
          renderInput={(params) => (
            <TextField {...params} label="Aggregation" data-sm="aggregation-input" />
          )}
          options={statisticOptions}
          getOptionLabel={(option) => StatisticOptionLabels[option]}
          renderOption={(props, option) => (
            <li {...props} key={option}>
              {StatisticOptionLabels[option]}
            </li>
          )}
          isOptionEqualToValue={(option, value) => option === value}
        />
      </Box>

      <YAxisRangePicker
        yAxisAuto={yAxisAuto}
        yAxisFrom={yAxisFrom}
        yAxisTo={yAxisTo}
        onChange={(patch) => onChangeItem(inputId, patch)}
      />

      {/* Close button visible only on screens >= 768px */}
      {closeButton({ '@media (max-width: 767px)': { display: 'none' } })}
    </Card>
  );
};
